import { Check, ChevronDown } from 'lucide-react';
import { Select as SelectPrimitive } from 'radix-ui';
import { createContext, useContext, useRef, type ComponentProps, type ReactNode, type RefObject } from 'react';
import { cn } from './cn.ts';

/**
 * Whether the open list was just closed by a press outside it. While the list
 * is open the page under it takes no pointer events, so a finger on the box
 * presses the sheet's overlay: Radix closes the list on that press, then the
 * tap's click lands on the box, now live again, and a touch click opens the
 * list (Joel's iPhone, 4 November: tapping the box again never closed it).
 * The box swallows that one click, and the flag ends with that tap.
 */
const ClosedByOutsidePress = createContext<RefObject<boolean> | null>(null);

/** shadcn's select on the kit's tokens: a 10 px box with a chevron, its list a white card with a tick on the picked line. */
export function UiSelect(props: ComponentProps<typeof SelectPrimitive.Root>) {
  const closedByOutsidePress = useRef(false);
  return (
    <ClosedByOutsidePress.Provider value={closedByOutsidePress}>
      <SelectPrimitive.Root {...props} />
    </ClosedByOutsidePress.Provider>
  );
}
export const UiSelectValue = SelectPrimitive.Value;

export function UiSelectTrigger({ className, children, onPointerDown, onClick, ...props }: ComponentProps<typeof SelectPrimitive.Trigger>) {
  const closedByOutsidePress = useContext(ClosedByOutsidePress);
  return (
    <SelectPrimitive.Trigger
      data-slot="select-trigger"
      onPointerDown={(event) => {
        if (closedByOutsidePress !== null) closedByOutsidePress.current = false;
        onPointerDown?.(event);
      }}
      onClick={(event) => {
        // preventDefault stops Radix's own click handler, which would open the list again.
        if (closedByOutsidePress?.current === true) {
          closedByOutsidePress.current = false;
          event.preventDefault();
        }
        onClick?.(event);
      }}
      className={cn(
        'inline-flex h-control cursor-pointer items-center justify-between gap-s2 rounded-control border border-line-strong bg-surface px-s3 text-body whitespace-nowrap text-ink focus-visible:border-brand focus-visible:outline-2 focus-visible:outline-offset-0 focus-visible:outline-brand data-[placeholder]:text-muted [&>span]:truncate',
        className,
      )}
      {...props}
    >
      {children}
      <SelectPrimitive.Icon asChild>
        <ChevronDown className="size-s4 shrink-0 text-ink-2" aria-hidden="true" />
      </SelectPrimitive.Icon>
    </SelectPrimitive.Trigger>
  );
}

export function UiSelectContent({ className, children, position = 'popper', onPointerDownOutside, ...props }: ComponentProps<typeof SelectPrimitive.Content>) {
  const closedByOutsidePress = useContext(ClosedByOutsidePress);
  return (
    <SelectPrimitive.Portal>
      <SelectPrimitive.Content
        data-slot="select-content"
        position={position}
        onPointerDownOutside={(event) => {
          onPointerDownOutside?.(event);
          // Only a touch or a pen: Radix opens on a mouse's press, never its click, and a mouse's click lands on no box.
          if (event.defaultPrevented || closedByOutsidePress === null || event.detail.originalEvent.pointerType === 'mouse') return;
          closedByOutsidePress.current = true;
          // The flag lives for this one tap: cleared once its click has reached every handler (the document hears it last), or when the touch turns into a scroll.
          const owner = event.detail.originalEvent.view?.document;
          if (owner === undefined) return;
          const clear = () => {
            closedByOutsidePress.current = false;
            owner.removeEventListener('click', clear);
            owner.removeEventListener('pointercancel', clear);
          };
          owner.addEventListener('click', clear, { once: true });
          owner.addEventListener('pointercancel', clear, { once: true });
        }}
        sideOffset={4}
        className={cn(
          'z-50 max-h-(--radix-select-content-available-height) min-w-(--radix-select-trigger-width) overflow-hidden rounded-control border border-line bg-surface font-sans text-body text-ink shadow-float data-[state=open]:animate-pop-in motion-reduce:animate-none',
          className,
        )}
        {...props}
      >
        <SelectPrimitive.Viewport className="p-s1">{children}</SelectPrimitive.Viewport>
      </SelectPrimitive.Content>
    </SelectPrimitive.Portal>
  );
}

export function UiSelectItem({ children, ...props }: ComponentProps<typeof SelectPrimitive.Item> & { readonly children: ReactNode }) {
  return (
    <SelectPrimitive.Item
      data-slot="select-item"
      className="relative flex min-h-control cursor-pointer items-center rounded-control pr-s7 pl-s3 outline-none select-none data-[highlighted]:bg-sunk data-[state=checked]:font-semibold"
      {...props}
    >
      <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
      <SelectPrimitive.ItemIndicator className="absolute right-s3 inline-flex items-center text-brand">
        <Check className="size-s4" aria-hidden="true" />
      </SelectPrimitive.ItemIndicator>
    </SelectPrimitive.Item>
  );
}
